import React, { useState, useEffect, useMemo } from "react";
import { Calendar, User, ArrowRight, Search, Loader2, Star } from "lucide-react";
import CommonButton from "../../components/CommonButton";
import { useNavigate } from "react-router-dom";
import { useLazyGetActiveBlogQuery } from "../../redux/actions/Customer/FooterCustomer";
import { Blog } from "../../types/Response/AdminResponse";

const THEME = {
    mainBrandColor: "#6DBE45",
    logoHeading: "#2F5E2E",
    accent: "#A8D97B",
};

export const BlogPage: React.FC = () => {
    const navigate = useNavigate();
    const [activeCategory, setActiveCategory] = useState<string>("All");
    const [searchQuery, setSearchQuery] = useState<string>("");

    // RTK Query Setup - Relying only on active blogs list to extract featured data inline
    const [triggerGetActiveBlogs, { data: activeResponse, isLoading, isError }] = useLazyGetActiveBlogQuery();

    // Unpack data wrappers accurately from server response envelopes (.value.data)
    const activeBlogs: Blog[] = activeResponse?.value?.data ?? [];

    useEffect(() => {
        triggerGetActiveBlogs();
    }, [triggerGetActiveBlogs]);

    // Extract categories cleanly out of the active data stream pool models properties
    const dynamicCategories = useMemo(() => {
        const discovered = activeBlogs.map((b) => b.metaTitle?.split("|")[0]?.trim() || "Pet Care");
        return ["All", ...Array.from(new Set(discovered))];
    }, [activeBlogs]);

    // Trace primary single featured item safely out of data blocks response values arrays
    const featuredPost = useMemo(() => {
        return activeBlogs.find((b) => b.isFeatured === true) || activeBlogs[0];
    }, [activeBlogs]);

    // Filter computation grid matrices logic lines
    const filteredPosts = useMemo(() => {
        return activeBlogs.filter((post) => {
            const matchesCategory = activeCategory === "All" || (post.metaTitle || "").includes(activeCategory);
            const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                post.shortDescription.toLowerCase().includes(searchQuery.toLowerCase());
            return matchesCategory && matchesSearch;
        });
    }, [activeBlogs, activeCategory, searchQuery]);

    return (
        <div className="max-w-7xl mx-auto px-4 py-10 space-y-12 animate-in fade-in duration-700 text-left">

            {/* 1. Header & Search Bar Section */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-6 border-b border-gray-100">
                <div>
                    <span className="text-xs uppercase tracking-widest font-black text-white bg-[#2F5E2E] px-3 py-1 rounded-lg">
                        Paws & Pages
                    </span>
                    <h1 className="text-3xl sm:text-4xl font-black text-[#2F5E2E] mt-2 tracking-tight">
                        The UAE Pet Journal 🐾
                    </h1>
                    <p className="text-gray-500 mt-2 font-medium">
                        Expert insights, municipality rules, and lifestyle advice for happy desert pets.
                    </p>
                </div>

                {/* Search Box */}
                <div className="relative w-full md:w-80">
                    <input
                        type="text"
                        placeholder="Search articles..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-11 pr-4 py-2.5 rounded-2xl border-2 border-slate-200 outline-none focus:border-[#6DBE45] bg-white shadow-xs text-sm font-medium transition"
                    />
                    <Search className="absolute left-4 top-3.5 text-gray-400 w-4 h-4" />
                </div>
            </div>

            {/* 2. Category Navigation Badges */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none">
                {dynamicCategories.map((cat) => (
                    <button
                        key={cat}
                        onClick={() => setActiveCategory(cat)}
                        className={`px-4 py-2 rounded-xl text-xs font-black tracking-wide uppercase transition whitespace-nowrap shrink-0 border ${activeCategory === cat
                            ? "bg-[#2F5E2E] border-[#2F5E2E] text-white shadow-xs"
                            : "bg-slate-50 border-slate-200 text-[#2F5E2E] hover:bg-slate-100 cursor-pointer"
                        }`}
                    >
                        {cat}
                    </button>
                ))}
            </div>

            {/* --- LOADING INDICATORS MASK BLOCKS --- */}
            {isLoading && (
                <div className="py-20 text-center text-xs font-semibold text-slate-400 italic flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin text-[#2F5E2E]" />
                    <span>Synchronizing content streams from Petland DXB...</span>
                </div>
            )}

            {/* --- ERROR FALLBACK HANDLING BOUNDARY --- */}
            {isError && (
                <div className="py-12 border border-rose-100 rounded-2xl bg-rose-50/30 text-rose-700 font-bold text-center text-xs">
                    Failed to recover pet journal entries. Please refresh workspace.
                </div>
            )}

            {/* 3. Main Content Layout Container */}
            {!isLoading && !isError && filteredPosts.length === 0 ? (
                <div className="text-center py-20 bg-slate-50/50 border border-dashed border-slate-200 rounded-3xl text-sm font-medium text-slate-400">
                    No matching articles found in this pack!
                </div>
            ) : null}

            {!isLoading && !isError && filteredPosts.length > 0 && (
                <div className="space-y-12">

                    {/* FEATURED POST HERO CARD PANEL VIEWS */}
                    {featuredPost && activeCategory === "All" && !searchQuery.trim() && (
                        <div 
                  onClick={() => navigate(`BlogDetails/${featuredPost.slug}`)}
                            className="bg-white rounded-[2rem] overflow-hidden border border-slate-200/80 shadow-3xs hover:shadow-2xs transition-all duration-300 grid grid-cols-1 lg:grid-cols-12 group cursor-pointer"
                        >
                            <div className="lg:col-span-7 h-64 sm:h-80 lg:h-[400px] overflow-hidden relative bg-slate-50">
                                {featuredPost.bannerImageUrl || featuredPost.thumbnailUrl ? (
                                    <img
                                        src={featuredPost.bannerImageUrl || featuredPost.thumbnailUrl}
                                        alt={featuredPost.title}
                                        className="w-full h-full object-cover group-hover:scale-101 transition duration-500"
                                    />
                                ) : null}
                                <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-xs px-3 py-1 rounded-xl text-[10px] font-black text-[#2F5E2E] uppercase tracking-wider border border-slate-100 shadow-xs flex items-center gap-1">
                                    <Star size={11} fill="currentColor" />
                                    <span>Featured Guide</span>
                                </div>
                            </div>

                            <div className="lg:col-span-5 p-6 sm:p-8 flex flex-col justify-between space-y-6">
                                <div className="space-y-3">
                                    <span className="text-[10px] font-black text-[#6DBE45] tracking-wider uppercase">
                                        Editor's Choice
                                    </span>
                                    <h2 className="text-xl sm:text-2xl font-black text-slate-900 leading-tight tracking-tight group-hover:text-[#6DBE45] transition-colors">
                                        {featuredPost.title === "string" ? "Best Food Options for Puppies" : featuredPost.title}
                                    </h2>
                                    <p className="text-slate-400 text-xs leading-relaxed font-medium line-clamp-3">
                                        {featuredPost.shortDescription === "string" ? "Discover the best healthy food variants options to build growth and long-term pet immunity maps." : featuredPost.shortDescription}
                                    </p>
                                </div>

                                <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
                                    <div className="flex items-center gap-2.5 text-[11px] text-slate-400 font-bold">
                                        <span className="flex items-center gap-1"><User size={13} className="text-[#2F5E2E]" /> {featuredPost.authorName === "string" ? "Petland Team" : featuredPost.authorName}</span>
                                        <span>•</span>
                                        <span className="flex items-center gap-1"><Calendar size={13} /> {new Date(featuredPost.publishedAt).toLocaleDateString()}</span>
                                    </div>
                                    <button className="p-2 rounded-xl bg-[#2F5E2E] text-white transition shrink-0 cursor-pointer">
                                        <ArrowRight size={14} className="stroke-[2.5]" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Secondary Grid Section */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredPosts
                            .filter((post) => (activeCategory !== "All" || searchQuery.trim() ? true : post.id !== featuredPost?.id))
                            .map((post) => (
                                <article
                                    key={post.id}
                                    onClick={() => navigate(`BlogDetails/${post.slug}`)}
                                    className="bg-white rounded-2xl overflow-hidden border border-slate-200/80 shadow-3xs hover:shadow-2xs transition duration-300 flex flex-col group cursor-pointer"
                                >
                                    <div className="relative h-48 overflow-hidden bg-slate-100 shrink-0">
                                        {post.thumbnailUrl && post.thumbnailUrl !== "string" ? (
                                            <img
                                                src={post.thumbnailUrl}
                                                alt={post.title}
                                                className="w-full h-full object-cover group-hover:scale-103 transition duration-500"
                                            />
                                        ) : (
                                            <div className="w-full h-full bg-slate-100 flex items-center justify-center text-slate-300 text-xs font-bold">Petland Guide</div>
                                        )}
                                    </div>

                                    <div className="p-4 sm:p-5 flex-1 flex flex-col justify-between space-y-4">
                                        <div className="space-y-1.5">
                                            <h3 className="text-sm sm:text-base font-black text-slate-900 tracking-tight line-clamp-2 min-h-[44px] group-hover:text-[#6DBE45] transition-colors">
                                                {post.title}
                                            </h3>
                                            <p className="text-slate-400 text-xs font-medium leading-relaxed line-clamp-3">
                                                {post.shortDescription}
                                            </p>
                                        </div>

                                        <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-[10px] sm:text-[11px] font-bold text-slate-400 gap-2">
                                            <span className="flex items-center gap-1 truncate"><User size={12} className="text-[#6DBE45] shrink-0" /> {post.authorName}</span>
                                            <span className="shrink-0">{new Date(post.publishedAt).toLocaleDateString()}</span>
                                        </div>
                                    </div>
                                </article>
                            ))}
                    </div>

                </div>
            )}

            {/* UAE Newsletter Footer Bar Integration */}
            <div className="bg-slate-50 border border-slate-200 rounded-[2rem] p-6 sm:p-10 text-center relative overflow-hidden mt-6">
                <h2 className="text-xl sm:text-2xl font-black text-[#2F5E2E] mb-2 uppercase">Never Miss a Beat 🐾</h2>
                <p className="text-xs sm:text-sm text-slate-500 max-w-lg mx-auto mb-6 font-medium">
                    Subscribe to get alerted instantly on critical Dubai Municipality health updates and local pet events.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
                    <input
                        type="email"
                        placeholder="Enter your email address"
                        className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 bg-white shadow-3xs text-xs font-semibold outline-none focus:border-[#6DBE45]"
                    />
                    <CommonButton
                        text="Subscribe"
                        className="bg-[#2F5E2E] text-white border-none rounded-xl h-10 font-bold px-6 shrink-0"
                    />
                </div>
            </div>

        </div>
    );
};